import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TrackByFunction,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Filters, NavTree, Results } from '@local/client-contracts';
import { ManualPromise } from '@local/common';
import { isEmbed, isExtension } from '@local/common-web';
import { getModifiers, isEnterKey, KeyName } from '@local/ts-infra';
import { PopupRef, PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BrowseEvent, FiltersList } from '@shared/components/filters/filters-list';
import { EmbedService } from '@shared/embed.service';
import { EventsService, WindowService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { isEqual } from 'lodash';
import { debounceTime, fromEvent, Observable, Subscription } from 'rxjs';
import { TagComponent } from 'src/app/bar/components/suggestions-tags/suggestion-tag/suggestion-tag.component';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { NavTreeService } from '../../services/nav-tree.service';
import { OptionsPopupComponent, OptionsPopupData } from '../../views/hub/components/launcher-avatar-popup/options-popup.component';
import { PCName } from '../../views/results/models/results-types';
import { pcAppName } from '../../views/results/utils/results.util';

export interface BrowseBarModel {
  items: Results.BrowseBarItem[];
  telemetryName: string;
  redirectPath: string;
  isAppsItemsModel?: boolean;
}
@UntilDestroy()
@Component({
  selector: 'browse-bar',
  templateUrl: './browse-bar.component.html',
  styleUrls: ['./browse-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrowseBarComponent implements OnInit, AfterViewInit, OnDestroy {
  isShowMoreAvailable: boolean = false;
  barItems: Results.BrowseBarItem[];
  itemsState: { [key: string]: { tooltip: string; hasRedDot: boolean } } = {};
  isAppsItemsModel: boolean;
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  _componentFocused: boolean = null;
  shortView: boolean = false;
  appsLimit: number = 0;

  private pcName = pcAppName();
  private keyHandlerId: string;
  private list: FiltersList;
  private optionsPopupRef: PopupRef<OptionsPopupComponent, OptionsPopupData>;
  private telemetryName: string;
  private redirectPath: string;
  private _focus: boolean = false;
  private afterViewInitPromise = new ManualPromise<void>();
  private interval: any = null;
  private embedInline: boolean;

  private readonly isEmbed = isEmbed();
  private readonly isExtension = isExtension();
  private readonly APPS_GAP = 8;
  private readonly MORE_BUTTON_WIDTH = 28;
  private readonly SMALL_WIDTH_APP = 28;

  @Input() set model(val: BrowseBarModel) {
    if (!val) {
      return;
    }
    this.telemetryName = val.telemetryName;
    this.redirectPath = val.redirectPath;
    this.isAppsItemsModel = val.isAppsItemsModel;
    if (!isEqual(val.items, this.barItems)) {
      this.barItems = val.items;
      this.buildItemsState();
      this.setBarItems();
    }
    this.cdr.markForCheck();
  }

  @Input() set componentFocused(val: boolean) {
    this.focusComponent(val);
  }
  @Input() isLauncher: boolean;
  @Input() enableWindowResize: boolean = false;

  @Input() set focus(res: boolean) {
    this._focus = res;
    if (res) {
      if (!this.list?.markedIndex) this.mark('first');
    } else {
      if (this.list?.isMarkedLastLine) this.unmark();
    }
  }

  @Output() componentFocusedFinish = new EventEmitter();

  @ViewChildren('optionElement') optionsRefs: QueryList<ElementRef | TagComponent>;
  @ViewChild('appsContainer') appsContainer: ElementRef;
  @ViewChild('moreApps') moreAppsRef: ElementRef;

  get focus(): boolean {
    return this._focus;
  }

  get focused() {
    return !!this.list.markedEl;
  }

  constructor(
    private routerService: RouterService,
    private eventsService: EventsService,
    private applicationsService: ApplicationsService,
    public barService: HubService,
    private keyboardService: KeyboardService,
    private filtersService: FiltersService,
    private navTreeService: NavTreeService,
    private cdr: ChangeDetectorRef,
    private popupService: PopupService,
    private window: WindowService,
    private embedService: EmbedService
  ) {}

  ngOnInit() {
    if (this._componentFocused === null) {
      this.registerKeyboardHandler();
    }
    this.pcName = (this.applicationsService.apps['pc']?.name as PCName) || this.pcName;
    if (this.isEmbed) {
      this.embedService.visible$.pipe(untilDestroyed(this)).subscribe(async (res) => {
        if (res) {
          this.setBarItems();
        }
      });
      this.setEmbedInline();
    }
  }

  async setEmbedInline() {
    this.embedInline = await this.embedService?.isInline();
  }

  ngAfterViewInit() {
    this.afterViewInitPromise.resolve();
    this.setBarItems();
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.pipe(debounceTime(100)).subscribe((evt) => {
      if (this.enableWindowResize) {
        this.setBarItems();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
    this.resizeSubscription$?.unsubscribe();
    this.stopInterval();
  }

  private focusComponent(val: boolean) {
    this._componentFocused = val;
    if (val) {
      this.registerKeyboardHandler(true);
    } else {
      this.list?.unmark();
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
  }

  private async setBarItems() {
    await this.afterViewInitPromise;
    if (!this.appsContainer || !this.optionsRefs || this.interval) {
      return;
    }
    this.list = new FiltersList(this.optionsRefs);

    this.appsContainer.nativeElement.classList.add('transparent');
    this.appsLimit = this.barItems?.length;
    this.shortView = false;
    this.cdr.markForCheck();

    this.interval = setInterval(() => this.setAppsLimit(), 10);
  }

  private stopInterval() {
    clearInterval(this.interval);
    this.interval = null;
  }

  private setAppsLimit() {
    if (!this.optionsRefs.toArray()?.length || !this.appsContainer?.nativeElement?.clientWidth) {
      return;
    }
    this.stopInterval();
    const appsRef = this.optionsRefs.toArray();
    const mainWidth = this.appsContainer.nativeElement.clientWidth - this.MORE_BUTTON_WIDTH;
    this.appsLimit = 0;
    let sumWidth: number = 0;

    appsRef.forEach((app: ElementRef | TagComponent) => {
      const width: number = (app as TagComponent)?.ref?.nativeElement?.offsetWidth || (app as ElementRef)?.nativeElement?.offsetWidth || 0;
      sumWidth += width + this.APPS_GAP;
      if (sumWidth <= mainWidth) {
        this.appsLimit++;
      }
    });

    if (this.appsLimit < this.barItems.length) {
      this.shortView = true;
      const appsWidth = appsRef.length * (this.SMALL_WIDTH_APP + this.APPS_GAP);
      if (appsWidth < mainWidth) {
        this.appsLimit = appsRef.length;
        this.isShowMoreAvailable = false;
      } else {
        this.appsLimit = this.barItems?.length - (appsWidth - mainWidth) / (this.SMALL_WIDTH_APP + this.APPS_GAP);
        this.isShowMoreAvailable = true;
      }
    } else {
      this.shortView = false;
      this.isShowMoreAvailable = false;
    }

    this.focusComponent(true);
    this.appsContainer.nativeElement.classList.remove('transparent');
    this.cdr.markForCheck();
  }

  onMoreAppsClicked() {
    const { x, y } = this.moreAppsRef.nativeElement.getBoundingClientRect();
    const offset: Partial<ConnectedPosition> = { offsetY: 38, offsetX: 0 };
    const position: ConnectedPosition[] = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top', ...offset }];

    const options: OptionsPopupData = { options: [] };

    this.barItems.slice(this.appsLimit)?.forEach((app) => {
      options.options.push({
        label: app.name,
        type: 'navigation',
        value: app.appId,
        icon: { type: 'img', value: app.icon.value },
      });
    });

    this.optionsPopupRef = this.popupService.open<OptionsPopupComponent, OptionsPopupData>({ x, y }, OptionsPopupComponent, options, {
      position,
    });
    this.optionsPopupRef.compInstance.close.subscribe(() => {
      this.optionsPopupRef.destroy();
    });
    this.optionsPopupRef.compInstance.invoke.subscribe((item) => {
      if (item.type === 'navigation') {
        this.navigateTo(
          this.barItems.find((i) => i.appId === item.value),
          'app'
        );
        this.optionsPopupRef.destroy();
      }
    });
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.focus) return;
    const modifiers = getModifiers(keys);
    const key = keys[0];

    if (key === 'tab') event.preventDefault();

    if (modifiers.length === 1 && modifiers[0] === 'shift' && key === 'tab') {
      this.mark('prev');

      event.stopPropagation();
      return;
    } else if (modifiers.length) {
      return;
    }

    if (key === 'tab' && this.list.markedIndex === null) {
      if (this.shortView) this.mark('first');
      event.stopPropagation();
      return;
    }

    if (key === 'ArrowRight' || key === 'tab') {
      event.stopPropagation();
      if (this.list.markedIndex === null) {
        if (this.shortView) this.list.mark('first');
        return;
      }
      this.mark('next');
      return;
    }

    if (key === 'ArrowLeft') {
      this.mark('prev');
      event.stopPropagation();
      return;
    }

    if (isEnterKey(key)) {
      if (this.list.markedIndex === null) return;

      const currId = this.list.markedEl?.dataset.id;
      if (!currId) return;

      if (currId === 'plus') {
        this.navigateTo('new');
        this.unmark();
        return;
      }

      const curr = this.barItems.find((f) => f.name + ':' + f.type === currId);
      if (!curr) return;

      this.navigateTo(curr);
      this.unmark();
      event.stopPropagation();
      return;
    }
  }

  isDisabled(item: Results.BrowseBarItem): boolean {
    const isPc = item.name === this.pcName || item.app === this.pcName;
    return item.syncStatus === 'Preparing' && !isPc;
  }

  onMouseover(ref: ElementRef) {
    const curr = this.optionsRefs.toArray().find((ref) => ref instanceof ElementRef && ref.nativeElement.classList.contains('marked'));

    if (!curr || !(curr instanceof ElementRef)) return;

    curr.nativeElement.classList.add('hide-name');
  }

  onMouseOut() {
    this.optionsRefs.toArray().forEach((ref) => ref instanceof ElementRef && ref.nativeElement.classList.remove('hide-name'));
  }

  private isNodeItem = (item: any, type: 'app' | 'view'): item is NavTree.Node => type === 'view';

  trackItem: TrackByFunction<Results.BrowseBarItem> = (index: number, item: Results.BrowseBarItem) => item.name + ':' + item.type;

  async navigateTo(item: Results.BrowseBarItem | NavTree.Node | 'new', type?: 'app' | 'view') {
    if ((item as Results.BrowseBarItem)?.syncStatus === 'Preparing' || this.appsContainer.nativeElement.classList.contains('transparent')) {
      return;
    }
    const location: string = this.barService.currentLocation;
    if (item === 'new') {
      this.routerService.navigateByUrl('connect');
      this.eventsService.event('app_bar_click', {
        name: this.telemetryName,
        target: 'connect_app',
        location: {
          title: location,
        },
      });
      return;
    }
    if (this.isNodeItem(item, type)) {
      this.eventsService.event('app_bar_click', {
        name: this.telemetryName,
        target: item.id,
        location: {
          title: location,
        },
      });
      const url = this.navTreeService.getNodeUrl(item);
      this.routerService.navigateByUrl(url);
      return;
    }

    if (this.isDisabled(item)) return;

    const route = ['connect', item.appId];
    this.eventsService.event('app_bar_click', {
      name: this.telemetryName,
      target: item.type === 'app' ? item.appId : item.name,
      label: item.stale ? 'stale' : item.syncStatus === 'Started' ? 'syncing' : 'synced',
      location: {
        title: location,
      },
    });

    if (item.stale && !item.hasResources && item.links > 1) {
      this.routerService.navigate(route, { queryParams: { source: 'apps', published: true } }, this.isExtension, this.isExtension);
    } else {
      const query = this.barService.inputQuery;
      if (!this.isEmbed || this.embedInline) {
        const url = this.buildUrlWithFilters(item);
        await this.routerService.navigateByUrl(url);
      }
      this.barService.setState('search-trigger', this.telemetryName);
      this.barService.query = query;
    }
  }

  private buildUrlWithFilters(item: Results.BrowseBarItem): string {
    const filters: Filters.Values = { [item.type]: [item.name] };
    const currentParams = new URLSearchParams(window.location.search || '');
    const filtersUrl = this.filtersService.getFiltersAsUrlParams(filters);
    const filterParams = new URLSearchParams(filtersUrl);

    filterParams.forEach((value, key) => {
      currentParams.set(key, value);
    });

    return `${this.redirectPath}?${currentParams.toString()}`;
  }

  private getItemTooltip(item: Results.BrowseBarItem): string {
    const hasResources = item.links === 0 || item.hasResources;

    if (item.type === 'app' && item.name === this.pcName) return;

    if (item.stale) return 'Outdated link.\r\n Go to Apps page to reconnect';

    if (item.syncStatus === 'Preparing') return 'Preparing to sync';

    if (item.syncStatus === 'Started') return 'Syncing';

    if (item.syncStatus === 'Failed') return 'sync failed';

    if (item.syncStatus === 'Succeeded' && !hasResources) return '0 Resources available';

    return;
  }

  private hasRedDot(item: Results.BrowseBarItem) {
    return (item.type === 'app' || item.type === 'service') && (item.stale || item.syncStatus === 'Failed');
  }

  private mark(event: BrowseEvent) {
    if (!this.list) {
      return;
    }
    if (!this.list.marked) {
      this.registerKeyboardHandler(true);
    }
    this.list.mark(event);
  }

  private unmark() {
    this.registerKeyboardHandler();
    this.list.unmark();
  }

  private buildItemsState() {
    if (!this.barItems?.length) return;
    this.barItems.forEach((item) => {
      this.itemsState = { ...this.itemsState, [item.appId]: { tooltip: this.getItemTooltip(item), hasRedDot: this.hasRedDot(item) } };
    });
  }

  private registerKeyboardHandler(topPriority: boolean = false) {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
    const priority = topPriority ? 7 : undefined;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), priority);
  }
}
